import { z } from 'zod';
import { dateSchema, idSchema, nameSchema, objectIdSchema, priceSchema } from '../../validation';
import { addressLocationSchema, amountSchema, partySchema, userTypeSchema } from '../../common';

//* event issuer
export const orderEventIssuerSchema = z.object({
  /**
   * this will be coming from the User Collection
   */
  _id: objectIdSchema,
  /** user type */
  type: userTypeSchema,
  /** user name */
  name: nameSchema,
});
export type OrderEventIssuer = z.infer<typeof orderEventIssuerSchema>;

//* event type
export const orderEventTypesSchema = z.enum([
  'order_created',
  'order_accepted',
  'order_is_confirmed',
  'unable_to_find_carrier',

  'ready_to_start_journey', // this will be used mainly when we have scheduled orders
  'journey_start', // when the driver/admin hits the start button

  //* driver
  'order_booked', // when the driver / carrier employee hits book_order button
  'searching_for_driver',
  'driver_assigned',
  'driver_accepted',
  'carrier_assigned', // carrier company assigned
  'driver_rest_start',
  'driver_rest_end',

  // pickup
  'items_picked_up',
  'in_transit',
  'arrived',

  // drop off
  'items_dropped_off',
  'destination_done', // done with the destination ... means the items are dropped off and invoice collected, and ready to go to the next
  'delivered', // the order is completed here
  /**
   * Successful short-lifecycle terminal (e.g. waybill-only / idle auto-close).
   * Distinct from `delivered` and cancel events.
   */
  'order_closed',

  'legal_prevention_time_start',
  'legal_prevention_time_end',

  // cancellation
  'customer_canceled_order',
  'super_admin_canceled_order',

  // pod
  'pod_owner_assigned',
  'pod_received',
  'pod_under_review',
  'pod_ready_to_be_sent',
  'pod_sent',
  'pod_delivered',
  // verification
  'order_verified', // when an admin verifies a delivered order

  // ──────────── Contract-leg state transitions (Phase 1) ────────────
  // Previously stored as `Order.contracts[i].events[]`. Flattened into the
  // order-wide timeline so the full audit trail of an Order is a single
  // array. Each entry pairs with `contractId` below and an `appliedOn`
  // Party identifying who triggered the transition. The leg-side enum
  // (`orderContractEventTypeSchema`) is the source of truth for the
  // `contract_*` suffixes.
  'contract_created',
  'contract_accepted',
  'contract_declined',
  'contract_countered',
  'contract_cancelled',
  'contract_completed',

  // ──────────── Contract-leg price finality ────────────
  // Emitted when a leg's load price is set or changed. Pair with
  // `priceAudit` on the same timeline row for full Amount snapshots.
  'contract_price_quoted',
  'contract_price_set_manual',
  'contract_price_edited',
  /**
   * A destination edit moved the order onto a different lane and the
   * caller chose to keep the existing price (`locationChangePricing:
   * 'keep_previous'`). Distinct from `contract_price_edited` because the
   * price did *not* move — what makes it audit-worthy is that the price
   * no longer corresponds to the lane it was quoted for.
   */
  'contract_price_held_on_route_change',

  // other
  'destination_location_changed', // when a destination location has changed
  'picture_taken',
  'problem_report',
  'note_update',
  'other',
]);
export type OrderEventTypes = z.infer<typeof orderEventTypesSchema>;

/** Snapshot payload for `contract_price_*` timeline rows. */
export const orderTimelinePriceAuditSchema = z.object({
  prevAmount: amountSchema.optional(),
  newAmount: amountSchema,
  routeId: objectIdSchema.optional(),
  quotedRoutePrice: priceSchema.optional(),
});
export type OrderTimelinePriceAudit = z.infer<typeof orderTimelinePriceAuditSchema>;

//* event
export const orderTimelineEventSchema = z.object({
  /** event issuer info */
  issuer: orderEventIssuerSchema,
  /** the order status when the event happen ... if the event is a status_change, then the status will be the new one immediately */
  event: orderEventTypesSchema,
  /** the target destinationId in which the event happen */
  destinationId: idSchema.optional(),
  /**
   * the contract leg this event belongs to. Set for `contract_*` events
   * (Phase 1 — `Order.contracts[i].events[]` is gone) and any future
   * leg-scoped commercial events. Pair with `appliedOn` (the Party that
   * triggered the transition) to reconstruct the leg's audit trail.
   *
   * `OrderProjectionService.projectOrderForViewer` uses this to filter
   * leg events to legs visible to the viewer (`buyer._id`/`seller._id`).
   */
  contractId: objectIdSchema.optional(),
  /** the date at which the event has happened */
  date: dateSchema,
  /** any special note that could be associated with the event. Ex: (we canceled the order because 1234, etc) */
  note: z.string().optional(),
  /**
   * the party that this event is applied to.
   *
   * Phase 1: migrated from `relationSchema` to {@link partySchema} so
   * `appliedOn.ids[]` is queryable as a tenant id alongside the
   * `Order.tenants[]` array. Tolerant readers should still call
   * `normalizeTarget(appliedOn.target)` before branching on the collection.
   */
  appliedOn: partySchema.optional(),
  /** the location in where this event happens.
   *
   * It's optional because some events does not have to have a location.
   *
   * But it would be required when arriving at a pickup or drop off
   */
  location: addressLocationSchema.optional(),
  /**
   * Full Amount snapshots for `contract_price_*` events — who changed
   * the load price, from what, to what, and (when relevant) the frozen
   * route quote at the time of the write.
   */
  priceAudit: orderTimelinePriceAuditSchema.optional(),
});
export type OrderTimelineEvent = z.infer<typeof orderTimelineEventSchema>;

//* timeline
export const orderTimelineSchema = z.array(orderTimelineEventSchema).min(1);
export type OrderTimeline = z.infer<typeof orderTimelineSchema>;
